import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Observable, Subscription, filter } from 'rxjs';
import { IOPayload } from 'src/app/model/io-payload';
import { Device } from 'src/app/model/device';
import { selectDeviceInfoGatewayById, selectOutputEdited, selectOutputEditing } from 'src/app/store/device/device.selector';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { Site } from 'src/app/model/site';
import { selectSites } from 'src/app/store/site/site.selector';
import { isMtmacModel, isMtmacStandardModel, isMtmacUsModel } from 'src/app/conf/device-models';
import { StandardNewValidator, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { MTMACSTD_MINFWVER_PHASEIII, MTMACUS_MINFWVER_PHASEIII } from 'src/app/conf/mtma-module';
import { DeviceInfo } from 'src/app/model/device-info';

@Component({
	templateUrl: './edit-output.dialog.component.html',
})

export class EditOutputDialogComponent implements OnInit, OnDestroy {
	outputEditing$: Observable<boolean | undefined>;
	outputEditingSub: Subscription | undefined;
	outputEdited$: Observable<boolean | undefined>;
	outputEditedSub: Subscription | undefined;

	multiIOForm: FormGroup;
	terminal: IOPayload;
	device: Device;
	inputEditing: boolean;

	timedEnabled: number;
	bistableEnabled: number;
	invertedEnabled: number; // latch/unlatch

	bistableEnabBool: boolean;
	timedEnabledBool: boolean;
	invertedEnabBool: boolean;

	stateActiveOpt: any[];

	currentSite: Site;
	sites: Site[];
	sites$: Observable<Site[] | undefined>;
	sitesSub: Subscription | undefined;

	parentDevice: Device;
	fwVersion: string;
	deviceParentInfo: DeviceInfo;
	deviceInfo$: Observable<DeviceInfo | undefined>;
	deviceInfoSub: Subscription | undefined;
	isIoMtma: boolean = false;
	isMtmaPhaseIII: boolean = false;

	loading: boolean = true;

	screenWidth: number;

	constructor(
		private store: Store<AppState>,
		private ref: DynamicDialogRef,
		public config: DynamicDialogConfig,
	) {
		if (config.data !== undefined && config.data.terminal !== undefined && config.data.device !== undefined) {
			this.terminal = config.data.terminal;
			this.device = config.data.device;
			this.parentDevice = null;
		} else {
			this.terminal = null;
			this.device = null;
			this.parentDevice = null;
		}
		this.stateActiveOpt = [
			{ value: 0, label: 'LOW' },
			{ value: 1, label: 'HIGH' }
		];
		this.initSelectors();
	}

	ngOnInit(): void {
		this.subscribeAll();
		this.buildForm();
		this.onResize();
		StandardNewValidator.setFirstCompile(true);
	}

	buildForm(): void {
		this.timedEnabled = this.terminal.CommandType & 2 ? 1 : 0;
		this.bistableEnabled = this.terminal.CommandType & 1 ? 1 : 0;
		this.invertedEnabled = this.terminal.CommandType && this.terminal.CommandType === 4 || this.terminal.CommandType === 5 || this.terminal.CommandType === 6 ? 1 : 0;
		this.timedEnabledBool = this.terminal.CommandType & 2 ? true : false;
		this.bistableEnabBool = this.terminal.CommandType & 1 ? true : false;
		this.invertedEnabBool = this.terminal.CommandType && this.terminal.CommandType === 4 || this.terminal.CommandType === 5 || this.terminal.CommandType === 6 ? true : false;
		this.multiIOForm = new FormGroup({
			// BISTABLE
			'bistable': new FormControl(this.bistableEnabBool),
			'activeState': new FormControl({ value: this.terminal.StateLabelActive, disabled: !this.bistableEnabled }, Validators.required),
			'inactiveState': new FormControl({ value: this.terminal.StateLabelInactive, disabled: !this.bistableEnabled }, Validators.required),
			'stateActive': new FormControl({ value: this.terminal.StateActive, disabled: !this.bistableEnabled }, Validators.required),
			'commandActive': new FormControl({ value: this.terminal.CommandLabelActivate, disabled: !this.bistableEnabled }, Validators.required),
			'commandInactive': new FormControl({ value: this.terminal.CommandLabelDeactivate, disabled: !this.bistableEnabled }, Validators.required),
			// TIMED
			'timed': new FormControl(this.timedEnabledBool),
			'commandDuration': new FormControl({
				value: this.terminal.CommandDuration,
				disabled: !this.timedEnabled
			},
				[Validators.required, Validators.min(1), Validators.max(240)]
			),
			'commandTimed': new FormControl({ value: this.terminal.CommandLabelTimed, disabled: !this.timedEnabled }, Validators.required),
			// INVERT LATCH / UNLATCH
			'inverted': new FormControl(this.invertedEnabBool),
			'commandLabelReverse': new FormControl({ value: this.terminal.CommandLabelReverse, disabled: !this.invertedEnabled }, Validators.required)
		});
	}

	initSelectors(): void {
		this.outputEditing$ = this.store.select(selectOutputEditing);
		this.outputEdited$ = this.store.select(selectOutputEdited);
		this.sites$ = this.store.select(selectSites);
		this.deviceInfo$ = this.store.select(selectDeviceInfoGatewayById);
	}

	subscribeAll(): void {
		this.outputEditingSub = this.outputEditing$?.subscribe(
			(inputEditing) => {
				this.inputEditing = inputEditing;
			}
		);
		this.outputEditedSub = this.outputEdited$?.pipe(filter(edit => !!edit))
			.subscribe(() => {
				this.resetClose();
			});
		this.sitesSub = this.sites$?.subscribe(
			(sites) => {
				this.sites = sites;
				this.sites.forEach(site => {
					if (this.device.SiteId === site.Id) {
						this.currentSite = site;
					}
				});
				this.isIoMtma = this.isIOMtma();
				if (!this.isIoMtma) {
					this.loading = false;
				} // if is true, we are checking if is phase III
			}
		);
	}

	checkIsMtmaPhaseIII(): boolean {
		if (this.isIoMtma && this.fwVersion !== undefined && this.fwVersion !== null && this.parentDevice !== undefined && this.parentDevice !== null) {
			let isIoMtmaIII: boolean = false;
			if (isMtmacStandardModel(this.parentDevice?.ModelId)) {
				isIoMtmaIII = this.fwVersion >= MTMACSTD_MINFWVER_PHASEIII;
			} else if (isMtmacUsModel(this.parentDevice?.ModelId)) {
				isIoMtmaIII = this.fwVersion >= MTMACUS_MINFWVER_PHASEIII;
			} else { // .. just in case
				isIoMtmaIII = false;
			}
			this.loading = false;
			return isIoMtmaIII;
		}
		return false;
	}

	ngOnDestroy(): void {
		this.outputEditingSub?.unsubscribe();
		this.outputEditedSub?.unsubscribe();
		this.sitesSub?.unsubscribe();
		this.deviceInfoSub?.unsubscribe();
	}

	resetClose(): void {
		this.store.dispatch(DeviceActions.ioDialogReset());
		this.closeDialog();
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.multiIOForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.multiIOForm.get(key).errors;
			if (controlErrors != null) {
				/* Object.keys(controlErrors).forEach(keyError => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				}); */
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	save(): void {
		const ioPayload: IOPayload = {
			OutputId: this.terminal.OutputId,
			StateLabelActive: this.f.activeState.value,
			StateLabelInactive: this.f.inactiveState.value,
			StateActive: this.f.stateActive.value,
			CommandLabelActivate: this.f.commandActive.value,
			CommandLabelDeactivate: this.f.commandInactive.value,
			CommandDuration: this.f.commandDuration.value,
			CommandLabelTimed: this.f.commandTimed.value,
			// inverted (latch/unlatch): third bit (true/false), timed (monostable): second bit (true/false), bistable: first bit (true/false)
			CommandType: this.f.inverted.value * 2 * 2 + this.f.timed.value * 2 + this.f.bistable.value,
			CommandLabelReverse: this.f.commandLabelReverse.value === null ? 'ACTIVATE' : this.f.commandLabelReverse.value
		};

		this.store.dispatch(DeviceActions.editMultiIO({ device: this.device, ioPayload }));
	}

	toggleBistableParams(enable: boolean): void {
		if (!enable) {
			this.f.activeState.disable();
			this.f.inactiveState.disable();
			this.f.stateActive.disable();
			this.f.commandActive.disable();
			this.f.commandInactive.disable();
		} else {
			this.f.activeState.enable();
			this.f.inactiveState.enable();
			this.f.stateActive.enable();
			this.f.commandActive.enable();
			this.f.commandInactive.enable();
		}
	}

	toggleTimedParams(enable: boolean): void {
		if (!enable) {
			this.f.commandTimed.disable();
			this.f.commandDuration.disable();
		} else {
			this.f.commandTimed.enable();
			this.f.commandDuration.enable();
		}
	}

	toggleInvertedParams(enable: boolean): void {
		if (!enable) {
			this.f.commandLabelReverse.disable();
		} else {
			this.f.commandLabelReverse.enable();
		}
	}

	isIOMtma(): boolean {
		if (this.device.ParentId !== undefined && this.device.ParentId !== null && 
			this.currentSite !== undefined && this.currentSite.Devices !== undefined && this.currentSite.Devices !== null && this.currentSite.Devices.length > 0) {
			this.parentDevice = this.currentSite.Devices.find(device => device.Id === this.device.ParentId);
			if (this.parentDevice !== undefined && this.parentDevice !== null) {
				const isParentMtma : boolean = isMtmacModel(this.parentDevice.ModelId);
				if (isParentMtma) {
					this.store.dispatch(DeviceActions.getDeviceInfoGatewayByDeviceId({ deviceId: this.parentDevice?.Id }));
					this.deviceInfoSub = this.deviceInfo$.pipe(filter(d => !!d))
						.subscribe(deviceInfo => {
							this.deviceParentInfo = deviceInfo;
							this.fwVersion = this.deviceParentInfo.FwVersion;
							this.isMtmaPhaseIII = this.checkIsMtmaPhaseIII();
						}
					);
				}
				return isParentMtma;
			}
		}
		return false;
	}

	get f(): any {
		return this.multiIOForm.controls;
	}

	closeDialog(): void {
		this.ref.close();
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	underTablet(): boolean {
		return StandardPageLayout.underTablet(this.screenWidth);
	}

	col12Responsive(): string {
		return StandardPageLayout.col6ResponsiveNoPadding(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	newShowErrors(type: EditOutputDialogComponent["f"], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorRequiredMessage(type: EditOutputDialogComponent["f"]): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-18rem');
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}
}
